import {
	DestroyRef,
	Directive,
	inject,
	Input,
	OnInit,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, distinctUntilChanged, map, switchMap } from 'rxjs';

import { UserFeatureFlags } from '@reach/interfaces/user/user-feature-flags';

import { SessionService } from '~app-client/core/services';
import { AttributeString, attributeStringEnumParser } from '~app-ui-new/utils';

@Directive({ selector: '[ngIfFeatureFlag]', standalone: true })
export class NgIfFeatureFlagDirective implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly templateRef = inject(TemplateRef<void>);
	private readonly viewContainer = inject(ViewContainerRef);
	private readonly sessionService = inject(SessionService);

	private readonly featureFlag$$ = new BehaviorSubject<UserFeatureFlags | null>(null);
	private readonly featureFlag$ = this.featureFlag$$.asObservable();

	@Input()
	public set ngIfFeatureFlag(featureFlag: AttributeString<UserFeatureFlags> | null) {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const value = attributeStringEnumParser(featureFlag!, UserFeatureFlags, null);
		if (this.featureFlag$$.value === value) {
			return;
		}

		this.featureFlag$$.next(value ?? null);
	}

	public get ngIfFeatureFlag(): UserFeatureFlags | null {
		return this.featureFlag$$.value;
	}

	private rendered = false;

	ngOnInit(): void {
		this.sessionService.user$
			.pipe(
				map((user) => {
					return user?.featureFlags ?? [];
				}),
				switchMap((userFf) => {
					return this.featureFlag$.pipe(
						map((requiredFf) => {
							return !!requiredFf && userFf.includes(requiredFf);
						})
					);
				}),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((hasFeatureFlag) => {
				if (!hasFeatureFlag) {
					this.viewContainer.clear();
					this.rendered = false;
				} else if (!this.rendered) {
					this.viewContainer.createEmbeddedView(this.templateRef);
					this.rendered = true;
				}
			});
	}
}
